import type { DemoApiClientOptions } from '../demo/DemoApiClient';
import type { ApiClient } from './ApiClient';
import type { HttpApiClientConfig } from './httpConfig';

/**
 * Which server a build talks to (ARCH §1.2, §8.2): the backend over HTTP, or the in-memory demo server.
 * An app chooses once, at start-up, and puts the client in a React context; nothing else knows which one it is.
 *
 * **"Simulate offline" is impossible for a live account, and that is a type rule here** (REQ-246 AC5, API §12.2):
 * the http arm declares `simulateOffline?: never` and `offline?: never`, so `createApiClient({ transport: 'http',
 * simulateOffline: true })` does not compile. The QA switch exists so the designed offline states can be seen in
 * a static snapshot; it may never be a thing a real account's client can be put into (T-208).
 */
export type ApiClientConfig =
  | ({ transport: 'http'; simulateOffline?: never; offline?: never } & HttpApiClientConfig)
  | ({ transport: 'demo' } & DemoApiClientOptions);

/**
 * Asynchronous on purpose: BOTH clients arrive by dynamic import, so a build pays only for the one it talks to
 * (ARCH §8.1 "dynamic import"). The demo server and its dataset were always a separate chunk; since T-322
 * (D-773 — 8.7 KB of `HttpApiClient` sat in every demo build's eager chunk, unused) the HTTP client is too —
 * which holds only because nothing imports `./HttpApiClient` statically: the `@sl/core/api` barrel exports its
 * config and constants from `httpConfig.ts`, never the class (a module the eager graph reaches statically is
 * inlined into that chunk, dynamic import or not).
 */
export async function createApiClient(config: ApiClientConfig): Promise<ApiClient> {
  if (config.transport === 'http') {
    const { HttpApiClient } = await import('./HttpApiClient');
    return new HttpApiClient(config);
  }
  const { DemoApiClient } = await import('../demo/index');
  return new DemoApiClient(config);
}
